import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
    Typography,
    Button,
    Row,
    Col,
    message,
    Space,
    Spin,
    Dropdown,
    Menu,
    Popover
} from 'antd';
import {
    FilePdfOutlined,
    FileImageOutlined,
    DownloadOutlined,
    QrcodeOutlined,
    MessageOutlined,
} from '@ant-design/icons';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { saveAs } from 'file-saver';
import { v4 as uuidv4 } from 'uuid';

import session from '../../utils/session';
import formatUtil from '../../utils/format';
import mixpanel from '../../utils/mixpanel';
import defaultConfig from '../../config';
import { getCompanyById } from '../../reducers/company/resources';

import TemplateGallery from './TemplateGallery';
import InspectorPanel from './InspectorPanel';
import CanvasStage from './CanvasStage';
import { generateTemplates } from './ApplyByTextTemplates';
import ColoredQRCode from './ColoredQRCode';
import scanToApplyIcon from '../../assets/qr/scantoapply.png';
import { updateCompany } from '../../reducers/company/resources';
import { uploadImage } from '../../reducers/uploads/resources';
import apiCore from '../../utils/api-core';

import './ApplyByTextUnified.css';
import PageTourButton from '../guide/PageTourButton';
import { getApplyByTextSteps } from '../guide/toursteps';

const { Title, Text } = Typography;
const apiHost = (defaultConfig.api.host || '').replace(/\/$/, '');
const getHiringLogoUrl = (companyId, cacheBuster = Date.now()) =>
    `${apiHost}/api/v3/company/${companyId}/hiring-sign-logo?t=${cacheBuster}`;

const TEMPLATE_SLUGS = {
    'template-modern-bold-01': 'classic',
    'template-corporate-modern-02': 'corporate-modern',
    'template-industrial-high-impact-03': 'industrial-highimpact',
};
const getTemplateSlug = (id) => TEMPLATE_SLUGS[id] || id;

const ApplyByTextUnified = ({ history }) => {
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [keyword, setKeyword] = useState('Jobs');
    const [defaultABT, setDefaultABT] = useState('');
    const [companyData, setCompanyData] = useState(null);
    const [exportingSign, setExportingSign] = useState(false);
    const [exportingQR, setExportingQR] = useState(false);
    
    // Canvas State
    const [formValues, setFormValues] = useState({
        logoUrl: null,
        brandName: "We're Hiring!",
        careerSiteUrl: "",
        subHeading: "Join our team today and build your career with us.",
        disclaimer: "Message and data rates may apply. Visit our site for more info."
    });
    const [activeTemplateId, setActiveTemplateId] = useState("template-modern-bold-01");
    const [zoom, setZoom] = useState(70);

    const activeTemplate = React.useMemo(() => {
        const templates = generateTemplates(keyword, defaultABT, formValues);
        return templates.find(t => t.id === activeTemplateId) || templates[0];
    }, [keyword, defaultABT, formValues, activeTemplateId]);

    const signRef = useRef(null);
    const qrRef = useRef(null);

    useEffect(() => {
        const userData = session.get('userData');
        if (!userData) {
            history.push('/login');
            return;
        }

        const {
            companyId,
            default_abt,
            parent_bot_abt: parentBotABT,
            keyword: userKeyword
        } = userData;

        const abt = formatUtil.forceFormat(parentBotABT || default_abt);

        if (!abt) {
            history.push('/inbox');
            return;
        }

        setDefaultABT(abt);
        setKeyword(userKeyword || 'Jobs');

        getCompanyById(companyId).then((result) => {
            let initialForms = { ...formValues };
            if (result && result.data && result.data.length > 0) {
                const data = result.data[0];
                setCompanyData(data);
                initialForms.brandName = data.brand_name || "We're Hiring!";
                initialForms.careerSiteUrl = data.career_site_url || "";
                initialForms.disclaimer = data.disclaimer || "Message and data rates may apply.";
                if (data.hiring_logo) {
                    initialForms.logoUrl = getHiringLogoUrl(companyId);
                }
                setFormValues(initialForms);
            }
            setActiveTemplateId("template-modern-bold-01");
            setInitialLoadComplete(true);
        }).catch(err => {
            console.error("Failed to fetch company details", err);
            message.error("Failed to load your settings.");
            setActiveTemplateId("template-modern-bold-01");
            setInitialLoadComplete(true);
        });
    }, [history]);

    const handleSelectTemplate = (template) => {
        setActiveTemplateId(template.id);
    };

    const handleFormChange = (key, value) => {
        setFormValues(prev => ({ ...prev, [key]: value }));
    };

    // Auto-save form fields to company (debounced) using v3
    useEffect(() => {
        if (!initialLoadComplete || !companyData) return;
        
        const timer = setTimeout(() => {
            apiCore.put(`/api/v3/company/${companyData.id}/hiring-sign`, {
                brand_name: formValues.brandName,
                career_site_url: formValues.careerSiteUrl,
                disclaimer: formValues.disclaimer
            }).catch(e => console.error("Failed to update company data", e));
        }, 1000);
        return () => clearTimeout(timer);
    }, [formValues.brandName, formValues.careerSiteUrl, formValues.disclaimer, companyData, initialLoadComplete]);

    const handleLogoUpload = async (file, onSuccess, onError) => {
        if (!companyData) return;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('companyId', companyData.id);

        try {
            await apiCore.post(`/api/v3/company/${companyData.id}/hiring-sign-logo`, formData);
            const nocache = Date.now();
            setFormValues(prev => ({ ...prev, logoUrl: getHiringLogoUrl(companyData.id, nocache) }));
            onSuccess("ok");
            message.success('Company logo updated successfully.');
        } catch (e) {
            console.error('Error uploading logo:', e);
            onError(new Error('Upload failed'));
            message.error('Failed to upload logo.');
        }
    };

    const handleRemoveLogo = async () => {
        if (!companyData) return;
        try {
            await apiCore.delete(`/api/v3/company/${companyData.id}/hiring-sign-logo`);
            setFormValues(prev => ({ ...prev, logoUrl: null }));
            message.success('Company logo removed.');
        } catch (e) {
            console.error('Error removing logo:', e);
            message.error('Failed to remove logo.');
        }
    };

    const exportSignAsPng = async () => {
        if (!signRef.current || !activeTemplate) return;
        mixpanel.track('ApplyByText:download hiring sign canvas', { type: 'png' });
        setExportingSign(true);

        const filterNode = (node) => {
            if (node.tagName === 'LINK' && node.href && node.href.includes('use.fontawesome.com')) {
                return false;
            }
            if (node.tagName === 'IMG' && node.src && node.src.includes('s3.us-west-2.amazonaws.com')) {
                return false;
            }
            if (node.dataset && node.dataset.exportHide === 'true') {
                return false;
            }
            return true;
        };

        try {
            // Need a fast timeout to let the UI hide the selection outlines
            setTimeout(async () => {
                const dataUrl = await toPng(signRef.current, { 
                    pixelRatio: 3, 
                    cacheBust: true,
                    filter: filterNode,
                    fontEmbedCSS: '',
                    imagePlaceholder: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
                    style: {
                        transform: 'none',
                        boxShadow: 'none',
                        margin: '0'
                    }
                });
                const link = document.createElement('a');
                link.download = `hiring-sign-${getTemplateSlug(activeTemplate.id)}.png`;
                link.href = dataUrl;
                link.click();
                setExportingSign(false);
            }, 100);
        } catch (e) {
            console.error(e);
            message.error('Failed to export sign as PNG.');
            setExportingSign(false);
        }
    };

    const exportSignAsPdf = async () => {
        if (!signRef.current || !activeTemplate) return;
        mixpanel.track('ApplyByText:download hiring sign canvas', { type: 'pdf' });
        setExportingSign(true);

        const filterNode = (node) => {
            if (node.tagName === 'LINK' && node.href && node.href.includes('use.fontawesome.com')) {
                return false;
            }
            if (node.tagName === 'IMG' && node.src && node.src.includes('s3.us-west-2.amazonaws.com')) {
                return false;
            }
            if (node.dataset && node.dataset.exportHide === 'true') {
                return false;
            }
            return true;
        };

        try {
            setTimeout(async () => {
                const dataUrl = await toPng(signRef.current, { 
                    pixelRatio: 3, 
                    cacheBust: true,
                    filter: filterNode,
                    fontEmbedCSS: '',
                    imagePlaceholder: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
                    style: {
                        transform: 'none',
                        boxShadow: 'none',
                        margin: '0'
                    }
                });
                // We lock page size to letter now.
                const doc = new jsPDF({ orientation: 'p', unit: 'in', format: 'letter' });
                doc.addImage(dataUrl, 'PNG', 0, 0, 8.5, 11);
                doc.save(`hiring-sign-${getTemplateSlug(activeTemplate.id)}.pdf`);
                setExportingSign(false);
            }, 100);
        } catch (e) {
            console.error(e);
            message.error('Failed to export sign as PDF.');
            setExportingSign(false);
        }
    };

    const getQRValue = () => {
        return `sms:${defaultABT}?body=${keyword}`;
    };

    const downloadQRSvg = () => {
        const svgEl = qrRef.current?.querySelector('svg');
        if (!svgEl) { message.error('QR code not found.'); return; }
        mixpanel.track('ApplyByText:download qr', { data: { type: 'svg' } });

        const svgData = new XMLSerializer().serializeToString(svgEl);
        const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        saveAs(blob, 'apply-qr-code.svg');
    };

    const downloadQRPng = async () => {
        const svgEl = qrRef.current?.querySelector('svg');
        if (!svgEl) { message.error('QR code not found.'); return; }
        mixpanel.track('ApplyByText:download qr', { data: { type: 'png' } });

        setExportingQR(true);
        try {
            const svgData = new XMLSerializer().serializeToString(svgEl);
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);

            const img = new Image();
            img.onload = () => {
                const scale = 4;
                const canvas = document.createElement('canvas');
                canvas.width = img.width * scale;
                canvas.height = img.height * scale;
                const ctx = canvas.getContext('2d');
                ctx.scale(scale, scale);
                ctx.drawImage(img, 0, 0);
                URL.revokeObjectURL(url);

                canvas.toBlob((blob) => {
                    saveAs(blob, 'apply-qr-code.png');
                    setExportingQR(false);
                }, 'image/png');
            };
            img.onerror = () => {
                URL.revokeObjectURL(url);
                message.error('Failed to generate PNG.');
                setExportingQR(false);
            };
            img.src = url;
        } catch (e) {
            console.error(e);
            message.error('Failed to export QR as PNG.');
            setExportingQR(false);
        }
    };

    const downloadQRPdf = async () => {
        const svgEl = qrRef.current?.querySelector('svg');
        if (!svgEl) { message.error('QR code not found.'); return; }
        mixpanel.track('ApplyByText:download qr', { data: { type: 'pdf' } });

        setExportingQR(true);
        try {
            const svgData = new XMLSerializer().serializeToString(svgEl);
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);

            const img = new Image();
            img.onload = () => {
                const scale = 4;
                const canvas = document.createElement('canvas');
                canvas.width = img.width * scale;
                canvas.height = img.height * scale;
                const ctx = canvas.getContext('2d');
                ctx.scale(scale, scale);
                ctx.drawImage(img, 0, 0);
                URL.revokeObjectURL(url);

                const dataUrl = canvas.toDataURL('image/png');
                const doc = new jsPDF({ orientation: 'p', unit: 'in', format: 'letter' });
                const qrSize = 4;
                const x = (8.5 - qrSize) / 2;
                const y = (11 - qrSize) / 2;
                doc.addImage(dataUrl, 'PNG', x, y, qrSize, qrSize);
                doc.save('apply-qr-code.pdf');
                setExportingQR(false);
            };
            img.onerror = () => {
                URL.revokeObjectURL(url);
                message.error('Failed to generate PDF.');
                setExportingQR(false);
            };
            img.src = url;
        } catch (e) {
            console.error(e);
            message.error('Failed to export QR as PDF.');
            setExportingQR(false);
        }
    };

    const exportMenu = (
        <Menu>
            <Menu.Item key="1" icon={<FilePdfOutlined />} onClick={exportSignAsPdf}>
                Export as PDF <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>Print-ready</Text>
            </Menu.Item>
            <Menu.Item key="2" icon={<FileImageOutlined />} onClick={exportSignAsPng}>
                Export as PNG <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>Share digitally</Text>
            </Menu.Item>
        </Menu>
    );

    const qrPopoverContent = (
        <Space direction="vertical" size="small">
            <Text strong style={{ fontSize: 13 }}>Download QR Code</Text>
            <Space size="small">
                <Button size="small" icon={<DownloadOutlined />} onClick={downloadQRSvg}>SVG</Button>
                <Button size="small" icon={<FileImageOutlined />} onClick={downloadQRPng} loading={exportingQR}>PNG</Button>
                <Button size="small" icon={<FilePdfOutlined />} onClick={downloadQRPdf} loading={exportingQR}>PDF</Button>
            </Space>
        </Space>
    );

    if (!initialLoadComplete || !activeTemplate) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#FAFAFA' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="apply-by-text-wrapper tour-abt-overview" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
            
            {/* Main Header */}
            <div
                style={{
                    background: 'linear-gradient(110deg, #F6FFED 0%, #E6F7FF 100%)',
                    borderBottom: '1px solid #E6E6E6',
                    padding: '12px 0px'
                }}
            >
                <Row gutter={[16, 16]} align="middle" style={{ maxWidth: 1400, margin: '0' }}>
                    <Col xs={24} md={16} lg={18}>
                        <Space direction='horizontal' display='flex' align='center' justify='center'>
                            <Popover 
                                content={qrPopoverContent}
                                trigger="click"
                                placement="bottom"
                            >
                                <div style={{ display: 'flex', cursor: 'pointer', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    <div ref={qrRef} style={{ background: '#fff', padding: 6, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: 4, display: 'inline-block' }}>
                                        <ColoredQRCode
                                            value={getQRValue()}
                                            size={80}
                                            errorLevel="H"
                                            color="#000000"
                                            bgColor="#ffffff"
                                            finderColor="#38b549"
                                            icon={scanToApplyIcon}
                                            iconSize={22}
                                        />
                                    </div>
                                </div>
                            </Popover>
                            <Space direction="vertical" size={4}>
                                <Title level={3} style={{ margin: 0, color: '#1f2937' }}>Apply by Text <PageTourButton steps={getApplyByTextSteps()} /></Title>
                                <Text style={{ fontSize: 15, color: '#4b5563' }}>
                                    <Popover 
                                        content={qrPopoverContent}
                                        trigger="click"
                                        placement="bottom"
                                    >
                                        <span style={{ cursor: 'pointer', color: '#08979c', textDecoration: 'underline' }}><DownloadOutlined /> Download your QR code</span>
                                    </Popover>
                                    {' '}or generate a printable Hiring Sign.
                                </Text>
                                <Space size={16}>
                                    <Space size={6}>
                                        <QrcodeOutlined style={{ color: '#08979c' }} />
                                        <Text type="secondary">Scannable QR code</Text>
                                    </Space>
                                    <Space size={6}>
                                        <MessageOutlined style={{ color: '#08979c' }} />
                                        <Text type="secondary">SMS Application Flow</Text>
                                    </Space>
                                </Space>
                            </Space>
                        </Space>
                    </Col>
                    <Col xs={24} md={8} lg={6} style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Dropdown overlay={exportMenu} placement="bottomRight" trigger={['click']}>
                            <Button type="primary" size="middle" loading={exportingSign}>
                                 Download Hiring Sign <DownloadOutlined />
                            </Button>
                        </Dropdown>
                    </Col>
                </Row>
            </div>

            {/* Sub Tools Header */}
            <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '8px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 10 }}>
                <Space size="large">
                    <Title level={5} style={{ margin: 0, color: '#1f2937' }}>Hiring Sign Builder</Title>
                    {/* Zoom / Canvas Controls */}
                    <Space size="small">
                         <Text type="secondary">Zoom</Text>
                         <Button size="small" onClick={() => setZoom(Math.max(20, zoom - 10))}>-</Button>
                         <Text style={{ width: 40, textAlign: 'center' }}>{zoom}%</Text>
                         <Button size="small" onClick={() => setZoom(Math.min(200, zoom + 10))}>+</Button>
                         <Button size="small" type="link" onClick={() => setZoom(70)}>Fit</Button>
                         <Button size="small" type="link" onClick={() => setZoom(100)}>100%</Button>
                    </Space>
                </Space>
            </div>

            {/* Editor Layout: 3 Columns */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                
                {/* Left: Templates */}
                <TemplateGallery 
                    activeTemplateId={activeTemplate.id} 
                    onSelectTemplate={handleSelectTemplate} 
                    keyword={keyword}
                    defaultABT={defaultABT}
                    formValues={formValues}
                />

                {/* Center: Canvas Stage (Preview Only) */}
                <div style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <CanvasStage 
                        signRef={signRef}
                        templateSettings={activeTemplate}
                        zoom={zoom}
                        keyword={keyword}
                        defaultABT={defaultABT}
                    />
                </div>

                {/* Right: Inspector Pane (Form Only) */}
                <InspectorPanel 
                    formValues={formValues}
                    onFormChange={handleFormChange}
                    onLogoUpload={handleLogoUpload}
                    onRemoveLogo={handleRemoveLogo}
                />

            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({});
const mapDispatchToProps = (dispatch) => ({});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ApplyByTextUnified));
